import { useCallback, useMemo, useState } from 'react'
import type { FormInstance } from 'antd/es/form'
import { Upload } from 'antd'
import type { UploadProps } from 'antd'
import { useNavigate } from 'react-router-dom'
import {
  acceptedFileTypes,
  MAX_LOGO_FILE_SIZE_BYTES,
  MAX_LOGO_FILE_SIZE_MB,
  buildClientPayloadFromForm,
} from '../clientFormShared'
import type { ClientFormValues } from '../clientFormShared'
import {
  useClientLogo,
  useDeleteClientLogoMutation,
  type UseUpdateClientMutationResult,
  type UseUploadClientLogoMutationResult,
} from '../hooks'
import type { ClientDetail, PbiGroupOption } from '../types'
import { useToast } from '@/contexts/ToastContext'
import type { AuthSession } from '@/features/auth/types'
import { fileToBase64 } from '@/lib/files'
import { updateSession } from '@/lib/session'

export function useClientLogoSection(clientId: string) {
  const { addToast } = useToast()
  const [logoBase64, setLogoBase64] = useState('')
  const [logoContentType, setLogoContentType] = useState('')
  const [logoFileName, setLogoFileName] = useState('')
  const [logoError, setLogoError] = useState('')
  const [logoDeleted, setLogoDeleted] = useState(false)

  const { data: clientLogo, isPending: clientLogoPending } = useClientLogo(clientId, Boolean(clientId))
  const deleteClientLogoMutation = useDeleteClientLogoMutation()

  const clearPendingLogo = useCallback(() => {
    setLogoBase64('')
    setLogoContentType('')
    setLogoFileName('')
    setLogoError('')
  }, [])

  const resetLogoState = useCallback(() => {
    setLogoBase64('')
    setLogoContentType('')
    setLogoFileName('')
    setLogoError('')
    setLogoDeleted(false)
  }, [])

  const displayedLogoUrl = !logoDeleted ? clientLogo?.url || '' : ''

  const deleteLogo = useCallback(async () => {
    try {
      const deleted = await deleteClientLogoMutation.mutateAsync(clientId)
      if (!deleted) {
        addToast('An error occured while deleting the logo.', 'error')
        return
      }
      setLogoDeleted(true)
      clearPendingLogo()
      addToast('Logo has been deleted', 'success')
    } catch {
      addToast('An error occured while deleting the logo.', 'error')
    }
  }, [addToast, clearPendingLogo, clientId, deleteClientLogoMutation])

  const logoUploadProps: UploadProps = useMemo(
    () => ({
      accept: acceptedFileTypes.join(','),
      showUploadList: false,
      beforeUpload: async (file) => {
        if (!acceptedFileTypes.includes(file.type)) {
          setLogoError('Acceptable formats are JPEG, JPG, PNG, TIFF, WEBP, BMP, or GIF.')
          return Upload.LIST_IGNORE
        }
        if (file.size > MAX_LOGO_FILE_SIZE_BYTES) {
          setLogoError(`Logo file is too large. Maximum file size is ${MAX_LOGO_FILE_SIZE_MB}MB.`)
          return Upload.LIST_IGNORE
        }
        try {
          const base64 = await fileToBase64(file as File)
          setLogoBase64(base64)
          setLogoContentType(file.type)
          setLogoFileName(file.name)
          setLogoError('')
        } catch {
          setLogoError('Unable to process selected logo file.')
        }
        return false
      },
    }),
    []
  )

  return {
    logoError,
    logoBase64,
    logoContentType,
    logoFileName,
    resetLogoState,
    clearPendingLogo,
    displayedLogoUrl,
    logoUploadProps,
    deleteLogo,
    deleteLogoPending: deleteClientLogoMutation.isPending,
    clientLogoPending,
  }
}

type UseEditClientSaveParams = {
  clientId: string
  editDetail: ClientDetail | null | undefined
  form: FormInstance<ClientFormValues>
  pbiGroups: PbiGroupOption[]
  logoBase64: string
  logoContentType: string
  authSession: AuthSession | null | undefined
  updateMutation: UseUpdateClientMutationResult
  uploadClientLogoMutation: UseUploadClientLogoMutationResult
}

export function useEditClientSave({
  clientId,
  editDetail,
  form,
  pbiGroups,
  logoBase64,
  logoContentType,
  authSession,
  updateMutation,
  uploadClientLogoMutation,
}: UseEditClientSaveParams) {
  const navigate = useNavigate()
  const { addToast } = useToast()

  const saveClient = useCallback(async () => {
    if (!clientId || !editDetail) return
    try {
      const values = await form.validateFields()
      const payload = buildClientPayloadFromForm(values, pbiGroups, logoBase64)

      await updateMutation.mutateAsync({
        ...payload,
        clientId,
      })

      if (logoBase64) {
        const uploaded = await uploadClientLogoMutation.mutateAsync({
          clientId,
          base64image: logoBase64,
          contentType: logoContentType,
        })
        if (!uploaded) {
          addToast('Client saved, but logo upload failed', 'warning')
          navigate('/clients')
          return
        }
      }

      addToast('Client saved successfully', 'success')

      if (authSession?.currentClientId === clientId) {
        updateSession({
          currentClientName: values.name,
          currentClientTypeId: values.typeId,
          currentClientShowProjectStatus: !!values.showProjectStatus,
          currentClientShowAdvisoryMapping: !!values.showAdvisoryMapping,
        })
      }

      navigate('/clients')
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Unable to save client'
      addToast(message, 'error')
    }
  }, [
    addToast,
    authSession?.currentClientId,
    clientId,
    editDetail,
    form,
    logoBase64,
    logoContentType,
    navigate,
    pbiGroups,
    updateMutation,
    uploadClientLogoMutation,
  ])

  const savePending = updateMutation.isPending || uploadClientLogoMutation.isPending

  return { saveClient, savePending }
}

type UseEditClientCancelParams = {
  form: FormInstance<ClientFormValues>
  resetLogoState: () => void
}

export function useEditClientCancel({ form, resetLogoState }: UseEditClientCancelParams) {
  const navigate = useNavigate()

  return useCallback(() => {
    resetLogoState()
    form.resetFields()
    navigate('/clients')
  }, [form, navigate, resetLogoState])
}
